import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Stack } from "@mui/system";
import ChartDataLabels from "chartjs-plugin-datalabels";
import React, { useMemo, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";

import {
  countHeroTotal,
  countOpponentTotal,
  countSubOpponentsTotal,
} from "@/lib/statistics-analytic-utils";
import { centerTextPlugin } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/chart-plugins/chart-pugins";
import {
  Currency,
  colorPool,
} from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { PeriodStatisticsDto } from "@/shared/api";

export function PieChartCustom({ data }: { data: PeriodStatisticsDto[] }) {
  const heroTotal = useMemo(() => countHeroTotal(data), [data]);

  const opponentTotal = useMemo(() => countOpponentTotal(data), [data]);
  const subOps = useMemo(() => countSubOpponentsTotal(data), [data]);

  const opsData = useMemo(() => {
    let loserRake = opponentTotal.loserRake;
    let loserRakeBB = opponentTotal.loserRakeBB;
    let loserRakeUsd = opponentTotal.loserRakeUsd;

    let winnerRake = opponentTotal.winnerRake;
    let winnerRakeBB = opponentTotal.winnerRakeBB;
    let winnerRakeUsd = opponentTotal.winnerRakeUsd;

    let winnerGrossProfit = opponentTotal.winnerGrossProfit;
    let winnerGrossProfitBB = opponentTotal.winnerGrossProfitBB;
    let winnerGrossProfitUsd = opponentTotal.winnerGrossProfitUsd;

    let losersGrossProfit = opponentTotal.loserGrossProfit;
    let losersGrossProfitBB = opponentTotal.loserGrossProfitBB;
    let losersGrossProfitUsd = opponentTotal.loserGrossProfitUsd;

    subOps.forEach((subOpponentsTotal) => {
      loserRake -= subOpponentsTotal.loserRake;
      loserRakeBB -= subOpponentsTotal.loserRakeBB;
      loserRakeUsd -= subOpponentsTotal.loserRakeUsd;

      winnerRake -= subOpponentsTotal.winnerRake;
      winnerRakeBB -= subOpponentsTotal.winnerRakeBB;
      winnerRakeUsd -= subOpponentsTotal.winnerRakeUsd;

      winnerGrossProfit -= subOpponentsTotal.winnerGrossProfit;
      winnerGrossProfitBB -= subOpponentsTotal.winnerGrossProfitBB;
      winnerGrossProfitUsd -= subOpponentsTotal.winnerGrossProfitUsd;

      losersGrossProfit -= subOpponentsTotal.loserGrossProfit;
      losersGrossProfitBB -= subOpponentsTotal.loserGrossProfitBB;
      losersGrossProfitUsd -= subOpponentsTotal.loserGrossProfitUsd;
    });

    return {
      loserRake,
      loserRakeBB,
      loserRakeUsd,
      winnerRake,
      winnerRakeBB,
      winnerRakeUsd,
      winnerNetProfit: winnerGrossProfit - winnerRake,
      winnerNetProfitBB: winnerGrossProfitBB - winnerRakeBB,
      winnerNetProfitUsd: winnerGrossProfitUsd - winnerRakeUsd,
      losersNetProfit: losersGrossProfit - loserRake,
      losersNetProfitBB: losersGrossProfitBB - loserRakeBB,
      losersNetProfitUsd: losersGrossProfitUsd - loserRakeUsd,
    };
  }, [opponentTotal, subOps]);

  const [currency, setCurrency] = useState<Currency>("Chips");

  const heroNetWin = useMemo(() => {
    switch (currency) {
      case "Chips":
        return heroTotal.hero_gross_profit - heroTotal.hero_rake;
      case "BB":
        return heroTotal.hero_gross_profit_bb - heroTotal.hero_rake_bb;
      case "Usd":
        return heroTotal.hero_gross_profit_usd - heroTotal.hero_rake_usd;
    }
  }, [currency, heroTotal]);

  const heroGross = useMemo(() => {
    switch (currency) {
      case "Chips":
        return heroTotal.hero_gross_profit;
      case "BB":
        return heroTotal.hero_gross_profit_bb;
      case "Usd":
        return heroTotal.hero_gross_profit_usd;
    }
  }, [currency, heroTotal]);

  const heroRake = useMemo(() => {
    switch (currency) {
      case "Chips":
        return heroTotal.hero_rake;
      case "BB":
        return heroTotal.hero_rake_bb;
      case "Usd":
        return heroTotal.hero_rake_usd;
    }
  }, [currency, heroTotal]);

  const loserRake = useMemo(() => {
    switch (currency) {
      case "Chips":
        return opsData.loserRake;
      case "BB":
        return opsData.loserRakeBB;
      case "Usd":
        return opsData.loserRakeUsd;
    }
  }, [currency, opsData.loserRake, opsData.loserRakeBB, opsData.loserRakeUsd]);

  const winnerNetProfit = useMemo(() => {
    switch (currency) {
      case "Chips":
        return opsData.winnerNetProfit;
      case "BB":
        return opsData.winnerNetProfitBB;
      case "Usd":
        return opsData.winnerNetProfitUsd;
    }
  }, [
    currency,
    opsData.winnerNetProfit,
    opsData.winnerNetProfitBB,
    opsData.winnerNetProfitUsd,
  ]);

  const winnerRake = useMemo(() => {
    switch (currency) {
      case "Chips":
        return opsData.winnerRake;
      case "BB":
        return opsData.winnerRakeBB;
      case "Usd":
        return opsData.winnerRakeUsd;
    }
  }, [currency, opsData.winnerRake, opsData.winnerRakeBB, opsData.winnerRakeUsd]);

  const losersNetProfit = useMemo(() => {
    switch (currency) {
      case "Chips":
        return opsData.losersNetProfit;
      case "BB":
        return opsData.losersNetProfitBB;
      case "Usd":
        return opsData.losersNetProfitUsd;
    }
  }, [
    currency,
    opsData.losersNetProfit,
    opsData.losersNetProfitBB,
    opsData.losersNetProfitUsd,
  ]);

  const subOppData = useMemo(
    () =>
      subOps.map((i) => {
        let result = { loserRake: 0, winnerRake: 0, winnerNetProfit: 0 };

        switch (currency) {
          case "Chips":
            result = {
              loserRake: i.loserRake,
              winnerRake: i.winnerRake,
              winnerNetProfit: i.winnerGrossProfit - i.winnerRake,
            };
            break;
          case "BB":
            result = {
              loserRake: i.loserRakeBB,
              winnerRake: i.winnerRakeBB,
              winnerNetProfit: i.winnerGrossProfitBB - i.winnerRakeBB,
            };
            break;
          case "Usd":
            result = {
              loserRake: i.loserRakeUsd,
              winnerRake: i.winnerRakeUsd,
              winnerNetProfit: i.winnerGrossProfitUsd - i.winnerRakeUsd,
            };
            break;
        }

        return result;
      }),
    [currency, subOps]
  );

  const doughnutData = useMemo(() => {
    if (heroNetWin >= 0 && heroRake >= 0) {
      const data = [loserRake, winnerNetProfit, winnerRake, heroNetWin, heroRake];
      return {
        labels: ["Loser Rake", "Winner NetWin", "Winners Rake", "Hero NetWin", "Hero Rake"],
        data,
        innerLabels: [
          {
            label: "Loser pie",
            value: data.reduce((acc, item) => {
              return acc + item;
            }, 0),
          },
        ],
      };
    }

    if (heroNetWin < 0 && heroNetWin + heroRake >= 0) {
      const data = [loserRake, winnerNetProfit, winnerRake, heroGross];
      return {
        labels: ["Loser Rake", "Winner NetWin", "Winners Rake", "Hero Gross"],
        data,
        innerLabels: [
          {
            label: "Loser pie",
            value: data.reduce((acc, item) => {
              return acc + item;
            }, 0),
          },
        ],
      };
    }

    if (heroNetWin < 0 && heroNetWin + heroRake < 0) {
      const data = [loserRake, winnerNetProfit, winnerRake, heroRake];
      return {
        labels: ["Loser Rake", "Winner NetWin", "Winners Rake", "Hero Rake"],
        data,
        innerLabels: [
          {
            label: "Loser pie",
            value: losersNetProfit,
          },
          {
            label: "Hero pie",
            value: heroNetWin,
          },
        ],
      };
    }

    const data = [
      heroNetWin,
      heroRake,
      loserRake,
      ...subOppData.map((i) => i.loserRake),
      winnerRake,
      ...subOppData.map((i) => i.winnerRake),
      winnerNetProfit,
      ...subOppData.map((i) => i.winnerNetProfit),
    ];
    return {
      labels: [
        "Hero NetWin",
        "Hero Rake",
        "Losers Rake",
        ...subOppData.map((_, idx) => `SubLoser${idx} Rake`),
        "Winners Rake",
        ...subOppData.map((_, idx) => `SubWinner${idx} Rake`),
        "Winners NetWin",
        ...subOppData.map((_, idx) => `SubWinner${idx} NetWin`),
      ],
      data,
      innerLabels: [
        {
          label: "Loser pie",
          value: data.reduce((acc, item) => {
            return acc + item;
          }),
        },
      ],
    };
  }, [
    loserRake,
    winnerNetProfit,
    winnerRake,
    heroNetWin,
    heroRake,
    subOppData,
    heroGross,
    losersNetProfit,
  ]);

  return (
    <>
      <FormControl sx={{ minWidth: 320 }} size="small">
        <InputLabel id="demo-simple-select-label">Currency</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={currency}
          label="Currency"
          onChange={(e) => setCurrency(e.target.value as Currency)}
        >
          <MenuItem value="Chips">Chips</MenuItem>
          <MenuItem value="BB">BB</MenuItem>
          <MenuItem value="Usd">Usd</MenuItem>
        </Select>
      </FormControl>

      <Stack height="400px">
        <Doughnut
          data={{
            labels: doughnutData.labels,
            datasets: [
              {
                label: currency,
                // @ts-expect-error valid
                context: {
                  innerLabels: doughnutData.innerLabels,
                },
                data: doughnutData.data,
                backgroundColor: colorPool,
                borderWidth: 1,
                datalabels: {
                  color: "#848484",
                  formatter: (value, ctx) => {
                    const data = ctx.chart.data.datasets[0].data as number[];
                    const total = data.reduce((sum, v) => sum + v, 0);
                    const percent = ((value / total) * 100).toFixed(1);
                    const label = ctx.chart.data.labels?.[ctx.dataIndex] || "unknown";
                    return `${label} ${percent}%`.split(" ").join("\n");
                  },
                  font: {
                    weight: "bold",
                    size: 10,
                  },
                  anchor: "end", // точка привязки у края сегмента
                  align: "end", // текст начинать вне сегмента
                  offset: -10, // дистанция в px от края сегмента
                  clamp: true,
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,

            layout: {
              padding: 50,
            },
            plugins: {
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (tooltipItem) {
                    const total = tooltipItem.dataset.data.reduce((sum, val) => sum + val, 0);
                    const percentage = ((tooltipItem.raw as number) / total) * 100;
                    const percentStr = `${percentage.toFixed(2)}%`;

                    return [`${currency}: ${tooltipItem.formattedValue}`, percentStr];
                  },
                },
              },
              legend: { position: "left" },
            },
          }}
          plugins={[
            ChartDataLabels,
            centerTextPlugin,
            // outsideLabelsPlugin,
          ]}
        />
      </Stack>

      <Stack height="400px">
        <Bar
          data={{
            labels: [
              "Hero NetWin",
              "Hero Rake",
              "Winners Rake",
              "Winners NetWin",
              "Losers Rake",
              "Loser NetWin",
            ],
            datasets: [
              {
                label: "Hero NetWin",
                data: [heroNetWin, 0, 0, 0, 0, 0],
                backgroundColor: "#8eecc7",
              },
              {
                label: "Hero Rake",
                data: [0, heroRake, 0, 0, 0, 0],
                backgroundColor: "#a6b5ff",
              },
              {
                label: "Winners Rake",
                data: [0, 0, winnerRake, 0, 0, 0],
                backgroundColor: "#ff9b9c",
              },
              {
                label: "Winners NetWin",
                data: [0, 0, 0, winnerNetProfit, 0, 0],
                backgroundColor: "#d5ff8a",
              },
              {
                label: "Losers Rake",
                data: [0, 0, 0, 0, loserRake, 0],
                backgroundColor: "#ffb97f",
              },
              {
                label: "Loser NetWin",
                data: [0, 0, 0, 0, 0, losersNetProfit],
                backgroundColor: "#A8E6FF",
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom" },
            },
            scales: {
              x: {
                stacked: true,
              },
              y: {
                stacked: true,
                ticks: {
                  callback: function (value) {
                    return value.toLocaleString(); // красиво отображать 100,000
                  },
                },
              },
            },
          }}
        />
      </Stack>
    </>
  );
}
